import { motion } from "framer-motion";
import { Sparkles, Award, ShieldCheck } from "lucide-react";
import { ASSETS } from "@/lib/site";

export default function ProductShowcase() {
  return (
    <section
      id="product"
      data-testid="product-showcase-section"
      className="relative z-10 py-24 md:py-32 bg-gradient-to-b from-[color:var(--relumia-surface)] to-[color:var(--relumia-base)] overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-32 top-1/3 h-[500px] w-[500px] rounded-full bg-[color:var(--relumia-gold)]/12 blur-[140px]" />
        <div className="absolute -left-24 bottom-1/4 h-[380px] w-[380px] rounded-full bg-[color:var(--relumia-gold)]/8 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-[0.58fr_0.42fr] gap-12 lg:gap-20 items-center">
        {/* Left: product image */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
          data-testid="product-image-block"
        >
          <div className="absolute inset-0 -z-10 flex items-center justify-center">
            <div className="h-[70%] w-[70%] rounded-full bg-[radial-gradient(circle,_rgba(197,160,89,0.22),transparent_65%)] blur-2xl" />
          </div>
          <img
            src={ASSETS.productHero}
            alt="Relumia Skin Lightening Tablet — 30 tablets, 3×10 blister pack"
            className="w-full h-auto object-contain"
          />
        </motion.div>

        {/* Right: brand copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Sparkles size={18} className="text-gold" strokeWidth={1.6} />
            <span className="overline !text-gold">The Relumia range</span>
          </div>
          <h2 className="font-serif font-light tracking-tight leading-[1.02] text-4xl md:text-5xl">
            Skin brightening, <span className="italic text-gold">from within.</span>
          </h2>
          <p className="mt-6 text-[15px] leading-relaxed text-[color:var(--relumia-muted)] max-w-md">
            Relumia is a celebrated glutathione tablet by JAKS Pharma — a dermatologist-friendly
            formulation of Japanese <span className="text-gold">OPITAC™</span> glutathione,
            Vitamin C, D3, Zinc and Curcuminoids in a single, once-daily tablet.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              [ShieldCheck, "OPITAC™ Japanese glutathione", "Non-GMO torula-yeast source · FDA GRAS notified"],
              [Award,       "Dermatologist-friendly formula", "L-Glutathione + Vit C, D3, Zinc, Curcuminoids"],
              [Sparkles,    "30 tablets · 3×10 blister",       "Once daily after a meal · advised by your clinician"],
            ].map(([Icon, title, hint]) => (
              <li key={title} className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color:var(--relumia-gold)]/12 border border-[color:var(--relumia-gold)]/40">
                  <Icon size={14} className="text-[color:var(--relumia-gold-deep)]" strokeWidth={1.8} />
                </span>
                <div>
                  <div className="text-[13.5px] text-[color:var(--relumia-ink)]">{title}</div>
                  <div className="text-[11.5px] text-[color:var(--relumia-muted)] mt-0.5">{hint}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="#authenticity"
              data-testid="product-cta-authentic"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-6 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors"
            >
              Verify authenticity
            </a>
            <a
              href="#contact"
              data-testid="product-cta-enquire"
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--relumia-ink)]/25 px-6 py-3 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors"
            >
              Enquire now
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
